import { tabKeyboard } from '@/lib/tabKeyboard';
import type { CSSProperties, ReactElement } from 'react';

// phase-fix-staging-assembly-buttons — 'package' (Package Viewer) moved to
// the Compiler: it views compiled output, meaningless pre-compile.
// T-17 (T17-D12) — the key set is owned by `@/lib/submissionTab` (the URL parser).
import type { SubmissionTabKey } from '@/lib/submissionTab';
export type { SubmissionTabKey };

export interface SubmissionTabsProps {
  readonly activeTab: SubmissionTabKey;
  readonly onTabChange: (tab: SubmissionTabKey) => void;
  /** phase-fix-submission-details-polish (review §2 #15) — tabs that do not
   * apply to this submission's region (e.g. Node Extensions on FDA, where
   * the validation policy blocks <node-extension> outright). */
  readonly hiddenTabs?: readonly SubmissionTabKey[];
}

interface TabDef {
  readonly key: SubmissionTabKey;
  readonly label: string;
}

const TABS: readonly TabDef[] = [
  { key: 'documents', label: 'Documents' },
  { key: 'forms', label: 'Admin Forms' },
  { key: 'lifecycle', label: 'Lifecycle History' },
  // Phase node-extension-compiler-ui (TODO-048 Part B)
  { key: 'node-extensions', label: 'Node Extensions' },
];

const tabBase: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid #cbd5e1',
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  borderBottom: '3px solid #cbd5e1',
  color: 'var(--text-muted)',
};

export function SubmissionTabs({ activeTab, onTabChange, hiddenTabs }: SubmissionTabsProps): ReactElement {
  const visibleTabs = hiddenTabs?.length ? TABS.filter((t) => !hiddenTabs.includes(t.key)) : TABS;
  return (
    <div
      role="tablist" onKeyDown={tabKeyboard}
      aria-label="Submission detail sections"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        borderBottom: '1px solid var(--border)',
        marginBottom: 0,
      }}
    >
      {visibleTabs.map((t) => {
        const isActive = t.key === activeTab;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-selected={isActive} tabIndex={(isActive) ? 0 : -1}
            aria-controls={`panel-${t.key}`}
            data-testid={`tab-${t.key}`}
            onClick={() => onTabChange(t.key)}
            style={{
              ...tabBase,
              color: isActive ? '#1B3A6B' : 'var(--text)',
              background: isActive ? '#e4ebf5' : 'var(--surface)',
              borderBottomColor: isActive ? '#1B3A6B' : '#cbd5e1',
              borderRadius: '5px 5px 0 0',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
